/**
 * Case-study "On this page" sidebar + "Copy link" button.
 * Builds the list from the article body's h2s, gives each one a stable id,
 * and marks the section the reader is currently in.
 */
(() => {
  const toc = document.querySelector('.rk-toc');
  const list = toc?.querySelector('[data-rk-toc-list]');
  const headings = [...document.querySelectorAll('.rk-article__content h2')];

  /** @param {string} text */
  const slugify = (text) =>
    text
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');

  if (toc && list && headings.length >= 3) {
    /** @type {Map<Element, HTMLAnchorElement>} */
    const links = new Map();

    for (const h of headings) {
      if (!h.id) h.id = slugify(h.textContent || '') || `section-${links.size + 1}`;
      const li = document.createElement('li');
      const a = document.createElement('a');
      a.href = `#${h.id}`;
      a.textContent = (h.textContent || '').trim();
      a.className = 'rk-toc__link';
      li.append(a);
      list.append(li);
      links.set(h, a);
    }
    toc.hidden = false;

    const content = document.querySelector('.rk-article__content');
    const scroller = toc.querySelector('.rk-toc__inner');
    /** @type {Element | null} */
    let current = null;

    /**
     * The current section is the last heading above the reading line (30% down
     * the viewport). Before the first heading, the first one stays lit. Once the
     * end of the article body is on screen, snap to the last heading, since
     * short closing sections never reach the reading line.
     */
    const pickCurrent = () => {
      const line = window.innerHeight * 0.3;
      const bodyEnd = content?.getBoundingClientRect().bottom ?? Infinity;
      if (bodyEnd <= window.innerHeight) return headings[headings.length - 1];

      let pick = headings[0];
      for (const h of headings) {
        if (h.getBoundingClientRect().top <= line) pick = h;
        else break;
      }
      return pick;
    };

    const update = () => {
      const next = pickCurrent();
      if (next === current) return;
      current = next;
      for (const [h, a] of links) {
        if (h === current) {
          a.setAttribute('aria-current', 'location');
          // Keep the lit link visible when the list itself scrolls (long TOCs).
          if (scroller && scroller.scrollHeight > scroller.clientHeight) {
            const top = a.offsetTop - scroller.clientHeight / 2;
            scroller.scrollTo({ top, behavior: 'smooth' });
          }
        } else {
          a.removeAttribute('aria-current');
        }
      }
    };

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        update();
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }

  for (const btn of document.querySelectorAll('[data-rk-copy-link]')) {
    btn.addEventListener('click', async () => {
      const label = btn.querySelector('[data-rk-copy-label]');
      const url = btn.getAttribute('data-rk-copy-link') || location.href;
      try {
        await navigator.clipboard.writeText(url);
        if (label) label.textContent = 'Copied';
      } catch {
        if (label) label.textContent = 'Copy failed';
      }
      setTimeout(() => {
        if (label) label.textContent = 'Copy link';
      }, 1800);
    });
  }
})();
